import { useEffect, useRef, useState } from 'react';

export const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim();

type TurnstileApi = {
  render: (element: HTMLElement, options: {
    sitekey: string;
    callback: (token: string) => void;
    'expired-callback': () => void;
    'error-callback': () => void;
    theme: 'auto';
    size: 'flexible';
  }) => string;
  remove: (id: string) => void;
};

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let scriptPromise: Promise<TurnstileApi> | undefined;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => window.turnstile
        ? resolve(window.turnstile)
        : reject(new Error('Verification unavailable'));
      script.onerror = () => { script.remove(); reject(new Error('Verification unavailable')); };
      document.head.appendChild(script);
    }).catch((error) => { scriptPromise = undefined; throw error; });
  }
  return scriptPromise;
}

export function Turnstile({ onToken, resetKey }: {
  onToken: (token: string) => void;
  resetKey: number;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!turnstileSiteKey) return;
    let cancelled = false;
    let widgetId: string | undefined;
    let api: TurnstileApi | undefined;
    onToken('');
    setError(false);
    loadTurnstile().then((loadedApi) => {
      if (cancelled || !container.current) return;
      api = loadedApi;
      widgetId = api.render(container.current, {
        sitekey: turnstileSiteKey,
        theme: 'auto',
        size: 'flexible',
        callback: (token) => { setError(false); onToken(token); },
        'expired-callback': () => onToken(''),
        'error-callback': () => { onToken(''); setError(true); },
      });
    }).catch(() => { if (!cancelled) setError(true); });
    return () => {
      cancelled = true;
      if (widgetId !== undefined) api?.remove(widgetId);
    };
  }, [onToken, resetKey]);

  if (!turnstileSiteKey) return null;
  return (
    <div className="space-y-2">
      <div ref={container} />
      {error && <p role="alert" className="text-sm text-destructive">Verification could not load. Please refresh the page and try again.</p>}
    </div>
  );
}
